import type { NetworkConfigResponse } from '@emma/api_client/types';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  type IButtonProps,
  Icon,
  Input,
  Popover,
  Portal,
  Text,
  Tooltip,
} from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { type MouseEvent, useMemo, useState } from 'react';
import { Loader } from '@/components/common/Loader';

export type AssignResult = {
  success: boolean;
  failures?: { networkConfigId: number; error: string }[];
};

type AssignNetworkConfigPopoverProps = IButtonProps & {
  availableConfigs: NetworkConfigResponse[];
  isLoading: boolean;
  hasError?: boolean;
  onAssign: (networkConfigIds: number[]) => Promise<AssignResult>;
};

const ConfigList = ({
  availableConfigs,
  filteredConfigs,
  selectedIds,
  onToggle,
  isLoading,
  hasError,
  configErrors,
}: {
  availableConfigs: NetworkConfigResponse[];
  filteredConfigs: NetworkConfigResponse[];
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  isLoading: boolean;
  hasError?: boolean;
  configErrors: Map<number, string>;
}) => {
  if (isLoading) return <Loader />;
  if (hasError)
    return (
      <Text color="red.500" fontSize="sm">
        Unable to load workspace network configs.
      </Text>
    );
  if (availableConfigs.length === 0)
    return (
      <Text color="gray.500" fontSize="sm">
        No network configs available to assign.
      </Text>
    );
  if (filteredConfigs.length === 0)
    return (
      <Text color="gray.500" fontSize="sm">
        No configs match your search.
      </Text>
    );

  return (
    <CheckboxGroup>
      {filteredConfigs.map((config) => {
        const errorMsg = configErrors.get(config.id);
        return (
          <Box key={config.id} p={1}>
            <Flex>
              <Checkbox isChecked={selectedIds.has(config.id)} onChange={() => onToggle(config.id)}>
                <Text fontSize="sm">
                  {config.label} ({config.fileName})
                </Text>
              </Checkbox>
            </Flex>
            {errorMsg && (
              <Text color="red.500" fontSize="xs" ml={6}>
                {errorMsg}
              </Text>
            )}
          </Box>
        );
      })}
    </CheckboxGroup>
  );
};

export const AssignNetworkConfigPopover = ({
  availableConfigs,
  isLoading,
  hasError,
  onAssign,
  ...buttonProps
}: AssignNetworkConfigPopoverProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [configErrors, setConfigErrors] = useState<Map<number, string>>(new Map());

  const filteredConfigs = useMemo(() => {
    if (!searchQuery.trim()) return availableConfigs;
    const query = searchQuery.toLowerCase();
    return availableConfigs.filter(
      (c) =>
        c.label.toLowerCase().includes(query) ||
        c.fileName.toLowerCase().includes(query) ||
        String(c.id).includes(query)
    );
  }, [availableConfigs, searchQuery]);

  const handleToggle = (configId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(configId)) {
        next.delete(configId);
      } else {
        next.add(configId);
      }
      return next;
    });
    setConfigErrors((prev) => {
      if (!prev.has(configId)) return prev;
      const next = new Map(prev);
      next.delete(configId);
      return next;
    });
  };

  const handleAssign = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedIds.size === 0) return;

    setIsAssigning(true);
    setConfigErrors(new Map());
    const result = await onAssign([...selectedIds]);
    setIsAssigning(false);

    if (result.success) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setConfigErrors(new Map());
      setIsOpen(false);
    } else if (result.failures) {
      const errors = new Map<number, string>();
      for (const failure of result.failures) {
        errors.set(failure.networkConfigId, failure.error);
      }
      setConfigErrors(errors);
      // Remove successfully assigned configs from selection
      const failedIds = new Set(result.failures.map((f) => f.networkConfigId));
      setSelectedIds((prev) => new Set([...prev].filter((id) => failedIds.has(id))));
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchQuery('');
    setConfigErrors(new Map());
    setIsOpen(false);
  };

  return (
    <Popover isLazy isOpen={isOpen} onClose={handleClose} placement="bottom-end">
      <Popover.Trigger>
        <Box onClick={(e: MouseEvent) => e.preventDefault()}>
          <Tooltip label="Assign network configs to this device" placement="left">
            <Button
              {...buttonProps}
              leftIcon={<Icon as={PlusBold} />}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
              size="sm"
              variant="outline"
            >
              Assign
            </Button>
          </Tooltip>
        </Box>
      </Popover.Trigger>
      <Portal>
        <Popover.Content onClick={(e: MouseEvent) => e.stopPropagation()} p={4} width="360px">
          <Popover.Arrow />
          <Popover.Body p={0}>
            <Input
              autoFocus
              mb={2}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search network configs..."
              size="sm"
              value={searchQuery}
            />
            <Box maxH="250px" overflowY="auto">
              <ConfigList
                availableConfigs={availableConfigs}
                configErrors={configErrors}
                filteredConfigs={filteredConfigs}
                hasError={hasError}
                isLoading={isLoading}
                onToggle={handleToggle}
                selectedIds={selectedIds}
              />
            </Box>
            {availableConfigs.length > 0 && (
              <Button
                isDisabled={selectedIds.size === 0}
                isLoading={isAssigning}
                mt={3}
                onClick={handleAssign}
                size="sm"
                variant="primary"
                width="100%"
              >
                Assign {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
              </Button>
            )}
          </Popover.Body>
        </Popover.Content>
      </Portal>
    </Popover>
  );
};
